'use client';

import { useCallback, useMemo } from 'react';
import { useRouter } from 'next/navigation';
import type { FrecencyEntry, FrecencyMeta } from '@/hooks/use-frecency';
import type {
  AestheticResult,
  AvatarResult,
  CommandDef,
  CommandOpenTab,
  MemberResult,
  MenuPage,
  OrgResult,
  OrgThreadResult,
  ProductResult,
  ProjectResult,
  SearchScope,
} from '../types';
import type { GlobalCommandMenuCopy } from '../global-command-menu-copy';
import { getGlobalCommandMenuGoToItems } from '../global-command-menu-navigation';
import { workspaceFilesAvatarDetailHref } from '@/lib/workspace-avatar-files-route';

interface UseGlobalCommandMenuActionsArgs {
  page: MenuPage;
  term: string;
  text: GlobalCommandMenuCopy;
  orgSlug?: string;
  organizationSlugById: ReadonlyMap<string, string>;
  close: () => void;
  pushPage: (page: MenuPage) => void;
  record: (key: string, meta: FrecencyMeta) => void;
}

export function useGlobalCommandMenuActions({
  page,
  term,
  text,
  orgSlug,
  organizationSlugById,
  close,
  pushPage,
  record,
}: UseGlobalCommandMenuActionsArgs) {
  const router = useRouter();

  const onNavigate = useCallback(
    (href: string, label?: string) => {
      if (label) record(`nav:${href}`, { label, type: 'navigation', href });
      router.push(href);
      close();
    },
    [close, record, router]
  );

  const onSelectProduct = useCallback(
    (product: ProductResult) => {
      const targetOrgSlug =
        orgSlug ??
        (product.organizationId
          ? organizationSlugById.get(String(product.organizationId))
          : undefined);
      if (!targetOrgSlug) return;
      const href = `/${targetOrgSlug}/products/${String(product._id)}`;
      record(`product:${String(product._id)}`, {
        label: product.name,
        type: 'product',
        href,
      });
      router.push(href);
      close();
    },
    [close, orgSlug, organizationSlugById, record, router]
  );

  const onSelectThread = useCallback(
    (thread: OrgThreadResult) => {
      if (orgSlug) {
        const href = `/${orgSlug}/threads/${thread.routeRef}`;
        record(`thread:${thread.routeRef}`, {
          label: thread.title || thread.threadId,
          type: 'thread',
          href,
        });
        router.push(href);
      }
      close();
    },
    [close, orgSlug, record, router]
  );

  const onSelectOrg = useCallback(
    (org: OrgResult) => {
      const href = `/${org.slug}`;
      record(`workspace:${org.slug}`, {
        label: org.name,
        type: 'workspace',
        href,
      });
      router.push(href);
      close();
    },
    [close, record, router]
  );

  const onSelectProject = useCallback(
    (project: ProjectResult) => {
      if (!orgSlug) return;
      const href = `/${orgSlug}/projects/${project._id}`;
      record(`project:${String(project._id)}`, {
        label: project.name,
        type: 'project',
        href,
      });
      router.push(href);
      close();
    },
    [close, orgSlug, record, router]
  );

  const onSelectAvatar = useCallback(
    (avatar: AvatarResult) => {
      if (!orgSlug) return;
      const href = workspaceFilesAvatarDetailHref({
        orgSlug,
        path: avatar.vfsFolderPath,
      });
      record(`avatar:${String(avatar._id)}`, {
        label: avatar.name,
        type: 'avatar',
        href,
      });
      router.push(href);
      close();
    },
    [close, orgSlug, record, router]
  );

  const onSelectAesthetic = useCallback(
    (aesthetic: AestheticResult) => {
      if (!orgSlug) return;
      const href = `/${orgSlug}/aesthetics/${String(aesthetic._id)}`;
      record(`aesthetic:${String(aesthetic._id)}`, {
        label: aesthetic.name,
        type: 'aesthetic',
        href,
      });
      router.push(href);
      close();
    },
    [close, orgSlug, record, router]
  );

  const onSelectMember = useCallback(
    (member: MemberResult) => {
      if (!orgSlug) return;
      const href = `/${orgSlug}/settings/members`;
      record(`member:${String(member.userId)}`, {
        label: member.name || member.email || '',
        type: 'member',
        href,
      });
      router.push(href);
      close();
    },
    [close, orgSlug, record, router]
  );

  const gotoItems = useMemo(
    () =>
      page.kind === 'root'
        ? getGlobalCommandMenuGoToItems({ orgSlug, term, text })
        : [],
    [orgSlug, page.kind, term, text]
  );

  const onSelectRecent = useCallback(
    (entry: FrecencyEntry) => {
      if (!entry.meta.href) return;
      record(entry.key, entry.meta);
      router.push(entry.meta.href);
      close();
    },
    [close, record, router]
  );

  const onPushScope = useCallback(
    (scope: SearchScope) => pushPage({ kind: 'scope', scope }),
    [pushPage]
  );

  const onRunCommand = useCallback(
    (command: CommandDef) => {
      if (command.subPage) {
        pushPage({
          kind: 'picker',
          commandId: command.id,
          label: command.label,
          subPage: command.subPage,
        });
        return;
      }
      record(`action:${command.id}`, { label: command.label, type: 'action' });
      close();
      void command.run?.();
    },
    [close, pushPage, record]
  );

  const onSelectOpenTab = useCallback(
    (tab: CommandOpenTab) => {
      close();
      tab.activate();
    },
    [close]
  );

  return {
    gotoItems,
    onNavigate,
    onPushScope,
    onRunCommand,
    onSelectAesthetic,
    onSelectAvatar,
    onSelectMember,
    onSelectOpenTab,
    onSelectOrg,
    onSelectProduct,
    onSelectProject,
    onSelectRecent,
    onSelectThread,
  };
}
